/**
 * Dining-room floor editor — PURE table-layout geometry.
 *
 * The layout is a fixed grid of cells (`FLOOR_GRID_COLS` × `FLOOR_GRID_ROWS`):
 * each placed table stores its cell in `dining_table.pos_x/pos_y` (integers,
 * NULL = not placed). Working in cells —and not pixels— makes the layout
 * independent of each terminal's screen size and deterministic to
 * test; the component translates cell ↔ pixel using the container's actual size.
 */

export const FLOOR_GRID_COLS = 12;
export const FLOOR_GRID_ROWS = 8;

export interface FloorCell {
  x: number;
  y: number;
}

/** Does the table have an assigned position on the layout? */
export function isPlaced(table: { pos_x: number | null; pos_y: number | null }): boolean {
  return table.pos_x !== null && table.pos_y !== null;
}

/** Clamps a cell to the grid boundaries. */
export function clampCell(
  x: number,
  y: number,
  cols = FLOOR_GRID_COLS,
  rows = FLOOR_GRID_ROWS,
): FloorCell {
  const cx = Math.min(Math.max(Math.trunc(x), 0), cols - 1);
  const cy = Math.min(Math.max(Math.trunc(y), 0), rows - 1);
  return { x: cx, y: cy };
}

/**
 * Translates a point in pixels (offset within the container) to its cell,
 * already clamped to the grid. `width`/`height` = container size.
 */
export function pointToCell(
  px: number,
  py: number,
  width: number,
  height: number,
  cols = FLOOR_GRID_COLS,
  rows = FLOOR_GRID_ROWS,
): FloorCell {
  if (width <= 0 || height <= 0) {
    return { x: 0, y: 0 };
  }
  return clampCell(Math.floor((px / width) * cols), Math.floor((py / height) * rows), cols, rows);
}

export function cellKey(x: number, y: number): string {
  return `${x},${y}`;
}

/**
 * Default position for the `index`-th table NOT placed: scans the grid in
 * reading order, skipping occupied cells. If the layout is full,
 * reuses the last cell (degenerate case: more tables than cells).
 */
export function autoPlaceCell(
  index: number,
  taken: ReadonlySet<string>,
  cols = FLOOR_GRID_COLS,
  rows = FLOOR_GRID_ROWS,
): FloorCell {
  let seen = 0;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (taken.has(cellKey(x, y))) {
        continue;
      }
      if (seen === index) {
        return { x, y };
      }
      seen++;
    }
  }
  return { x: cols - 1, y: rows - 1 };
}

/**
 * Resolves the visible cell of EACH table: placed tables use their persisted
 * cell (clamped for robustness); unplaced tables are auto-assigned to
 * free cells in stable order. Pure: same input → same layout.
 */
export function resolveFloorLayout(
  tables: readonly { id: string; pos_x: number | null; pos_y: number | null }[],
  cols = FLOOR_GRID_COLS,
  rows = FLOOR_GRID_ROWS,
): Map<string, FloorCell> {
  const out = new Map<string, FloorCell>();
  const taken = new Set<string>();
  for (const t of tables) {
    if (isPlaced(t)) {
      const cell = clampCell(t.pos_x ?? 0, t.pos_y ?? 0, cols, rows);
      out.set(t.id, cell);
      taken.add(cellKey(cell.x, cell.y));
    }
  }
  let i = 0;
  for (const t of tables) {
    if (!isPlaced(t)) {
      const cell = autoPlaceCell(i, taken, cols, rows);
      out.set(t.id, cell);
      i++;
    }
  }
  return out;
}
